export type SubscriptionPlan = {
  planName: string;
  autoRenew: boolean;
  /** ISO date (YYYY-MM-DD) or null when not scheduled. */
  nextBillingDate: string | null;
  monthlyAmount: number;
  currency: string;
};

export type PaymentMethod = {
  brand: string;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  holderName: string;
};

export type UsageQuota = {
  used: number;
  limit: number;
};

function parseIsoDate(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export function parseSubscriptionPlan(raw: unknown): SubscriptionPlan | null {
  if (!raw || typeof raw !== 'object') return null;

  const source = raw as Record<string, unknown>;
  const planName = typeof source.planName === 'string' ? source.planName.trim() : '';
  const monthlyAmount = Number(source.monthlyAmount);
  const currency = typeof source.currency === 'string' ? source.currency.trim().toUpperCase() : '';

  if (!planName || !Number.isFinite(monthlyAmount) || monthlyAmount < 0 || !currency) return null;

  return {
    planName,
    autoRenew: isTruthyFlag(source.autoRenew),
    nextBillingDate: parseIsoDate(source.nextBillingDate),
    monthlyAmount,
    currency,
  };
}

export function parsePaymentMethod(raw: unknown): PaymentMethod | null {
  if (!raw || typeof raw !== 'object') return null;

  const source = raw as Record<string, unknown>;
  const brand = typeof source.brand === 'string' ? source.brand.trim() : '';
  const last4 = typeof source.last4 === 'string' ? source.last4.trim() : '';
  const holderName = typeof source.holderName === 'string' ? source.holderName.trim() : '';
  const expiryMonth = Number(source.expiryMonth);
  const expiryYear = Number(source.expiryYear);

  if (!brand || !/^\d{4}$/.test(last4) || !holderName) return null;
  if (!Number.isInteger(expiryMonth) || expiryMonth < 1 || expiryMonth > 12) return null;
  if (!Number.isInteger(expiryYear) || expiryYear < 2000) return null;

  return { brand, last4, expiryMonth, expiryYear, holderName };
}

export function parseUsageQuota(raw: unknown): UsageQuota | null {
  if (!raw || typeof raw !== 'object') return null;

  const source = raw as Record<string, unknown>;
  const used = Number(source.used);
  const limit = Number(source.limit);

  if (!Number.isFinite(used) || used < 0) return null;
  if (!Number.isFinite(limit) || limit < 0) return null;

  return { used, limit };
}
